import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import ReactTooltip from 'react-tooltip';
import config from 'utils/config';

import { ActionButton } from 'components/ui/Button';
import { ErrorField } from 'pages/ManageClubEurostar/ManageClubEurostar.styles';
import {
  ACCESSIBLE_TRAVEL_TYPE,
  ANIMALS_ASSISTANCE_TYPE,
  GENERAL_TYPE,
  BLOCKED_SEATS_TYPE,
  EVACUATION_CHAIR_TYPE
} from 'constants/addPassengers';
import { TooltipContainer } from 'pages/ClubEurostarMember/MemberInfo/MemberInfo.styles';
import {
  PassengerTypeActionContainer,
  PassengerTypeContainer,
  PassengerTypeLabel,
  StyledAnimalsAlreadyBooked,
  StyledAnimalsAlreadyBookedContent
} from './AddPassengersCta.styles';
import { getNonGroupPassengerTypeOptions } from './AddPassengersCta.utils';
import SpecialAssistanceBooking from './SpecialAssistanceBookings';
import GeneralBookings from './GeneralBookings';
import AnimalsBookings from './AnimalsBookings';
import BlockedSeats from './BlockedSeats';
import EvacuationChairBookings from './EvacuationChairBookings';

const NonGroupBookings = ({
  register,
  passengersType,
  setPassengerType,
  errors,
  hasSnapTickets,
  hasWheelchairPassenger,
  hasWheelchairCompanion,
  hasNonWheelchairCompanion,
  actionComponent,
  bookingHasAnimals,
  isContinentalBooking,
  isLoyaltyBooking,
  isDutyTollFree,
  isTravelAgent,
  getValues
}) => {
  const IS_EVACUATION_CHAIR_ENABLED = config('ENABLE_EVACUATION_CHAIR', { canOverride: true }) === 'true';
  const handleGeneralClick = () => setPassengerType(GENERAL_TYPE);
  const handleAssistanceClick = () => setPassengerType(ACCESSIBLE_TRAVEL_TYPE);
  const handleAnimalsAssistanceClick = () => setPassengerType(ANIMALS_ASSISTANCE_TYPE);
  const handleBlockedSeatsClick = () => setPassengerType(BLOCKED_SEATS_TYPE);
  const handleEvacuationChairClick = () => setPassengerType(EVACUATION_CHAIR_TYPE);
  const {
    canShowGeneral,
    canShowSpecialAssistance,
    canShowAnimals,
    isAnimalsDisabled,
    shouldShowAnimalsAlreadyBooked,
    canShowBlockedSeats,
    canShowEvacuationChair
  } = getNonGroupPassengerTypeOptions({
    bookingHasAnimals,
    hasSnapTickets,
    hasWheelchairPassenger,
    hasWheelchairCompanion,
    hasNonWheelchairCompanion,
    isContinentalBooking,
    isLoyaltyBooking,
    isDutyTollFree,
    isTravelAgent,
    isEvacuationChairEnabled: IS_EVACUATION_CHAIR_ENABLED
  });

  if (passengersType === GENERAL_TYPE) {
    return (
      <>
        <GeneralBookings register={register} errors={errors} />
        {actionComponent}
      </>
    );
  }

  if (passengersType === BLOCKED_SEATS_TYPE) {
    return (
      <>
        <BlockedSeats register={register} errors={errors} />
        {actionComponent}
      </>
    );
  }

  if (passengersType === ANIMALS_ASSISTANCE_TYPE) {
    return (
      <>
        <AnimalsBookings register={register} errors={errors} />
        {errors.guideDogs ? (
          <ErrorField
            data-testid="assistance-animal-passengers-error"
            data-cy="assistance-animal-passengers-error"
            id="assistance-animal-passengers-error"
          >
            <FormattedMessage id="addPax.onlyOneAssistanceAnimal" />
          </ErrorField>
        ) : null}
        {actionComponent}
      </>
    );
  }

  if (passengersType === ACCESSIBLE_TRAVEL_TYPE) {
    return (
      <>
        <SpecialAssistanceBooking
          register={register}
          errors={errors}
          hasWheelchairPassenger={hasWheelchairPassenger}
          hasWheelchairCompanion={hasWheelchairCompanion}
        />

        {actionComponent}

        {errors.nonWheelchairCompanions ? (
          <ErrorField id="assistance-non-wheelchair-companions-error">
            <FormattedMessage id="addPassengers.maxCompanionsError" />
          </ErrorField>
        ) : null}
      </>
    );
  }

  if (passengersType === EVACUATION_CHAIR_TYPE) {
    return (
      <>
        <EvacuationChairBookings register={register} errors={errors} getValues={getValues} />
        {actionComponent}
      </>
    );
  }

  return (
    <PassengerTypeContainer>
      <PassengerTypeLabel>
        <FormattedMessage id="addPassengers.passengerTypeLabel" />
      </PassengerTypeLabel>
      <PassengerTypeActionContainer>
        {canShowGeneral && (
          <ActionButton data-testid="general-select" data-cy="general-select" onClick={handleGeneralClick}>
            <FormattedMessage id="addPassengers.general" />
          </ActionButton>
        )}
        {canShowSpecialAssistance && (
          <ActionButton data-testid="special-assistance-select" data-cy="special-assistance-select" onClick={handleAssistanceClick}>
            <FormattedMessage id="addPassengers.specialAssistance" />
          </ActionButton>
        )}

        {shouldShowAnimalsAlreadyBooked ? (
          <>
            <StyledAnimalsAlreadyBooked
              data-testid="animals-assistance-select"
              data-cy="animals-assistance-select"
              data-for="animals-tooltip"
              data-tip
            >
              <StyledAnimalsAlreadyBookedContent>
                <FormattedMessage id="addAssistance.assistanceAnimal" />
              </StyledAnimalsAlreadyBookedContent>
            </StyledAnimalsAlreadyBooked>
            <ReactTooltip className="tooltip" id="animals-tooltip" data-cy="animals-tooltip" place="bottom">
              <TooltipContainer>
                <FormattedMessage id="addPax.assistanceAnimalAlreadyBooked" />
              </TooltipContainer>
            </ReactTooltip>
          </>
        ) : (
          canShowAnimals && (
            <ActionButton
              data-testid="animals-assistance-select"
              data-cy="animals-assistance-select"
              onClick={handleAnimalsAssistanceClick}
              disabled={isAnimalsDisabled}
            >
              <FormattedMessage id="addAssistance.assistanceAnimal" />
            </ActionButton>
          )
        )}

        {canShowBlockedSeats && (
          <>
            <ActionButton data-testid="blocked-seats-select" data-cy="blocked-seats-select" onClick={handleBlockedSeatsClick}>
              <FormattedMessage id="addPassengers.blockedSeats" />
            </ActionButton>

            {canShowEvacuationChair && (
              <ActionButton data-testid="evacuation-chair-select" data-cy="evacuation-chair-select" onClick={handleEvacuationChairClick}>
                <FormattedMessage id="wheelchairSearch.answers.evac" />
              </ActionButton>
            )}
          </>
        )}
      </PassengerTypeActionContainer>
    </PassengerTypeContainer>
  );
};

NonGroupBookings.propTypes = {
  register: PropTypes.func,
  passengersType: PropTypes.oneOf([GENERAL_TYPE, ACCESSIBLE_TRAVEL_TYPE, null]),
  setPassengerType: PropTypes.func,
  errors: PropTypes.shape({
    wheelchairAdults: PropTypes.object,
    wheelchairChild: PropTypes.object,
    wheelchairCompanions: PropTypes.object,
    nonWheelchairCompanions: PropTypes.object,
    guideDogs: PropTypes.object
  }),
  hasSnapTickets: PropTypes.bool,
  hasNonWheelchairCompanion: PropTypes.bool,
  hasWheelchairPassenger: PropTypes.bool,
  hasWheelchairCompanion: PropTypes.bool,
  actionComponent: PropTypes.node,
  bookingHasAnimals: PropTypes.bool,
  isContinentalBooking: PropTypes.bool,
  isLoyaltyBooking: PropTypes.bool,
  isDutyTollFree: PropTypes.bool,
  isTravelAgent: PropTypes.bool,
  getValues: PropTypes.func
};

export default NonGroupBookings;
